import { useCallback, useEffect, useState } from 'react';
import { Paper, Typography, Checkbox, FormControlLabel, Button, Box } from '@mui/material';
import { getNoticesPopupAPI } from 'apis/notice/noticeAPI';
import Draggable from 'react-draggable';
import Tiptap from 'components/Tiptap/Tiptap';
import { useTranslation } from 'react-i18next';

interface NoticePopups {
  id: number;
  title: string;
  content: string;
  createdAt: string; // ISO 8601 날짜 형식
  createdBy: number;
  updatedAt: string; // ISO 8601 날짜 형식
  updatedBy: number;
  isFixed: boolean;
  isHidden: boolean;
  isPopup: boolean;
  language: string; // 예: "ko"
  popupExpiredAt: string; // ISO 8601 날짜 형식
  viewCount: number;
  attachments: null | any; // attachments가 null이거나 다른 형태일 경우 대응
}

export default function NoticePopup() {
  const [notices, setNotices] = useState<NoticePopups[]>([]);
  const [openPopups, setOpenPopups] = useState<{ [key: number]: boolean }>({});
  const [dontShowToday, setDontShowToday] = useState<{ [key: number]: boolean }>({});
  const [focusedPopupId, setFocusedPopupId] = useState<number | null>(null); // 포커싱된 팝업 ID
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용

  const fetchNotices = async () => {
    const res = await getNoticesPopupAPI();

    const today = new Date().toISOString().split('T')[0];
    const filteredNotices = res.data.filter((notice: NoticePopups) => {
      const hideDate = localStorage.getItem(`hidePopup_${notice.id}`);
      return hideDate !== today; // 오늘 날짜와 숨김 날짜가 다르면 표시
    });

    setNotices(filteredNotices);

    const initialPopups = filteredNotices.reduce((acc: { [key: number]: boolean }, notice: NoticePopups) => {
      acc[notice.id] = true;
      return acc;
    }, {});
    setOpenPopups(initialPopups);

    // 기본 포커스는 마지막 팝업에 설정
    if (filteredNotices.length > 0) {
      setFocusedPopupId(filteredNotices[filteredNotices.length - 1].id);
    }
  };

  const handleDontShowTodayChange = (id: number, checked: boolean) => {
    setDontShowToday((prev) => ({ ...prev, [id]: checked }));
  };

  const handleClose = (id: number) => {
    if (dontShowToday[id]) {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD 형식
      localStorage.setItem(`hidePopup_${id}`, today);
    }
    setOpenPopups((prev) => ({ ...prev, [id]: false }));

    // 닫힌 팝업이 포커싱된 팝업이라면, 이전 팝업을 포커싱
    if (id === focusedPopupId) {
      const visiblePopups = notices.filter((notice) => openPopups[notice.id]);
      const currentIndex = visiblePopups.findIndex((notice) => notice.id === id);

      // 이전 팝업으로 포커싱 이동, 없으면 포커싱 해제
      if (currentIndex > 0) {
        setFocusedPopupId(visiblePopups[currentIndex - 1].id);
      } else {
        setFocusedPopupId(null); // 더 이상 포커싱할 팝업이 없으면 null 처리
      }
    }
  };

  // ESC 키로 포커싱된 팝업 닫기
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedPopupId !== null) {
        handleClose(focusedPopupId);
      }
    },
    [focusedPopupId]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    fetchNotices();
  }, []);

  // 팝업 클릭 시 포커싱 설정
  const handleFocus = (id: number) => {
    setFocusedPopupId(id);
  };

  // 모든 팝업이 닫혔는지 확인
  const allPopupsClosed = Object.values(openPopups).every((isOpen) => !isOpen);

  // 팝업 배열이 없거나 모든 팝업이 닫힌 경우 컴포넌트 숨기기
  if (notices.length === 0 || allPopupsClosed) {
    return null;
  }

  return (
    <>
      {
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.4)', // 배경을 약간 어둡게 설정
            zIndex: 999, // 다른 요소보다 위에 배치
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflowY: 'auto' // 여러 팝업이 많을 경우 스크롤 가능
          }}
        >
          {notices.map(
            (notice, index) =>
              openPopups[notice.id] && (
                <Draggable
                  key={notice.id}
                  cancel=".non-draggable" // 드래그 제외 클래스
                >
                  <Paper
                    onMouseDownCapture={() => handleFocus(notice.id)} // 클릭 시 포커싱
                    onClick={() => handleFocus(notice.id)} // 클릭 시 포커싱
                    elevation={focusedPopupId === notice.id ? 5 : 3} // 포커싱된 팝업의 시각 효과 강화
                    sx={{
                      position: 'fixed',
                      top: `${120 + index * 30}px`,
                      left: `${200 + index * 30}px`,
                      transform: 'translateX(-50%)',
                      zIndex: focusedPopupId === notice.id ? 1000 : 10, // 최상위 z-index 설정
                      minWidth: '400px',
                      width: 'auto',
                      height: 'auto',
                      padding: '16px',
                      borderRadius: '8px',
                      boxShadow: focusedPopupId === notice.id ? '0px 4px 16px rgba(0, 0, 0, 0.4)' : '0px 4px 12px rgba(0, 0, 0, 0.2)',

                      // 모바일 전용 스타일
                      '@media (max-width: 768px)': {
                        top: '50%', // 모바일 화면 중앙
                        left: '50%', // 모바일 화면 중앙
                        transform: 'translate(-50%, -50%) !important', // 화면 중앙 정렬
                        minWidth: '400px', // 모바일에서 최소 너비 조정
                        maxWidth: '90vw', // 모바일 화면을 넘지 않도록 제한
                        maxHeight: '90vh', // 모바일 화면에서 높이 제한
                        overflowY: 'auto'
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                      [ {t('notice.pop_up.title')} ] {notice.title}
                    </Typography>
                    {/* TipTap Editor로 콘텐츠 렌더링 */}
                    <Box className="non-draggable">
                      <Tiptap content={notice.content} editable={false} maxHeight='500px' minWidth='400px' maxWidth='calc(100vw - 460px)' />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <FormControlLabel
                        className="non-draggable" // 드래그에서 제외
                        control={
                          <Checkbox
                            className="non-draggable" // 드래그에서 제외
                            checked={dontShowToday[notice.id] || false}
                            onChange={(e) => handleDontShowTodayChange(notice.id, e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.8rem' }}>{t('notice.pop_up.dont_show_today')}</Typography>}
                      />
                      <Button
                        className="non-draggable" // 드래그에서 제외
                        variant="contained" color="primary" onClick={() => handleClose(notice.id)} sx={{ fontSize: '0.8rem', borderRadius: '4px' }}>
                        {t('notice.pop_up.close')}
                      </Button>
                    </Box>
                  </Paper>
                </Draggable>
              )
          )}
        </Box>
      }
    </>
  );
}
